import type { JSX } from "preact";
import { useState } from "preact/hooks";

import { type Session, api, can } from "../api";
import { Button, Card, Empty, ErrorNote, Loading, Pill } from "../components/Ui";
import { datetime } from "../format";
import { useApi } from "../useApi";

interface DocumentInfo {
  name: string;
  path: string;
  kind: string;
  chars: number;
}

interface SourceFile {
  name: string;
  path: string;
  role: string;
  detail: string;
  chars: number;
}

interface Instructions {
  live: string;
  in_source: string;
  files: string[];
  updated_at: string;
  pending: boolean;
}

interface Status {
  source: string;
  wordpress_enabled: boolean;
  documents: DocumentInfo[];
  files: SourceFile[];
  instructions: Instructions;
  templates: number;
  skipped: string[];
  issues: string[];
}

/**
 * Mit kezd a rendszer egy-egy fájllal a forrásmappában.
 *
 * A szerep felismerése automatikus — ezért kell látszania. Enélkül egy fájl
 * csak „eltűnik”: a sablondokumentum nem szerepelt a dokumentumok között, és
 * nem volt egyértelmű, beolvasta-e a rendszer.
 */
const ROLE: Record<string, { label: string; tone: string }> = {
  dokumentum: { label: "tudásbázis", tone: "info" },
  sablon: { label: "sablonok", tone: "info" },
  utasitas: { label: "utasítás az AI-nak", tone: "warn" },
  arlista: { label: "árlista", tone: "ok" },
  csomagok: { label: "csomaglista", tone: "ok" },
  beallitasok: { label: "beállítások", tone: "ok" },
  gyors_kerdesek: { label: "gyors kérdések (chat gombjai)", tone: "ok" },
  kihagyva: { label: "kihagyva", tone: "muted" },
  nem_tamogatott: { label: "nem olvasható típus", tone: "bad" },
  hibas: { label: "hibás", tone: "bad" },
};

const KIND: Record<string, { label: string; tone: string }> = {
  weboldal: { label: "weboldal", tone: "info" },
  sablon: { label: "sablonok", tone: "info" },
  dokumentum: { label: "dokumentum", tone: "muted" },
};

export function KnowledgeView({ session }: { session: Session | null }): JSX.Element {
  const { data, error, loading, reload } = useApi<Status>("/admin/v1/knowledge");
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const sync = async (): Promise<void> => {
    setBusy(true);
    setMessage(null);
    try {
      const result = await api.post<{ summary: string }>("/admin/v1/knowledge/sync");
      setMessage(result.summary || "A szinkron lefutott.");
      reload();
    } catch (e: unknown) {
      setMessage(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (loading && !data) return <Loading />;
  if (!data) return <></>;
  // Frissítés közben a régi szerver még e mezők nélkül válaszolhat.
  const files = data.files ?? [];

  return (
    <>
      <Card
        title="Tudásbázis"
        hint={`Forrás: ${data.source}`}
        actions={
          can(session, "szinkronizal") && (
            <Button onClick={sync} disabled={busy}>
              {busy ? "Fut…" : "Újraszinkronizálás"}
            </Button>
          )
        }
      >
        {message && <p className="notice notice--info">{message}</p>}

        <p className="muted">
          A chat ebből válaszol: {data.documents.length} dokumentum
          {data.wordpress_enabled ? (
            <span>, a jóváhagyott WordPress oldalakkal együtt.</span>
          ) : (
            <span>. A WordPress kapcsolat ki van kapcsolva.</span>
          )}{" "}
          A keresés minden kérdés előtt lefut.
        </p>

        {data.documents.length === 0 ? (
          <Empty>
            A forrásban nincs feldolgozható dokumentum. Tegyen Word, PDF vagy szöveges fájlt a
            megosztott mappába.
          </Empty>
        ) : (
          <table className="rows">
            <thead>
              <tr>
                <th>Dokumentum</th>
                <th>Eredet</th>
                <th className="num">Terjedelem</th>
              </tr>
            </thead>
            <tbody>
              {data.documents.map((doc) => {
                const kind = KIND[doc.kind] ?? { label: doc.kind, tone: "muted" };
                return (
                  <tr key={doc.path}>
                    <td>
                      <strong>{doc.name}</strong>
                      {doc.path !== doc.name && <div className="muted mono">{doc.path}</div>}
                    </td>
                    <td>
                      <Pill tone={kind.tone}>{kind.label}</Pill>
                    </td>
                    <td className="num muted">{doc.chars.toLocaleString("hu-HU")} karakter</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {data.instructions && <InstructionsCard instructions={data.instructions} />}

      {files.length > 0 && (
        <Card
          title="Fájlok a forrásmappában"
          hint="Minden fájl, és amire a rendszer használja. A szerepet a rendszer ismeri fel."
        >
          <table className="rows">
            <thead>
              <tr>
                <th>Fájl</th>
                <th>Mire használjuk</th>
                <th>Részletek</th>
              </tr>
            </thead>
            <tbody>
              {files.map((file) => {
                const role = ROLE[file.role] ?? { label: file.role, tone: "muted" };
                return (
                  <tr key={`${file.path}:${file.role}`}>
                    <td>
                      <strong>{file.name}</strong>
                    </td>
                    <td>
                      <Pill tone={role.tone}>{role.label}</Pill>
                    </td>
                    <td className="muted">
                      {file.detail ||
                        (file.chars ? `${file.chars.toLocaleString("hu-HU")} karakter` : "")}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {data.issues.length > 0 && (
        <Card title="Megjegyzések a betöltésről">
          <ul className="plain">
            {data.issues.map((i, index) => (
              <li key={index} className="muted">
                {i}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}

/**
 * A rendelő állandó utasításai az AI-nak.
 *
 * A mappában az „utasítás” nevű dokumentum nem kereshető tartalom: minden
 * beszélgetésben, minden válasz előtt érvényes. Azonnal él, ezért itt mindig
 * az látszik, ami MOST a chatben van — és külön jelezzük, ha a forrásban
 * már más áll.
 */
function InstructionsCard({ instructions }: { instructions: Instructions }): JSX.Element {
  return (
    <Card
      title="Állandó utasítások az AI-nak"
      hint="A chat minden válaszánál érvényesek. Forrásuk a mappa „utasítás” nevű dokumentuma; a szinkron után azonnal élnek."
    >
      {instructions.pending && (
        <p className="notice notice--warn">
          A dokumentum tartalma megváltozott a legutóbbi szinkron óta. Az új szöveg az
          „Újraszinkronizálás” után él.
        </p>
      )}
      <p className="muted">
        Az utasítások a chat <strong>szöveges válaszaira</strong> hatnak. Hogy egy beutalón vagy
        egy árkérdésben melyik megnevezés melyik vizsgálatot jelenti, azt az árlista „egyéb
        nevei” oszlopa dönti el — egy új rövidítést oda érdemes felvenni.
      </p>
      {instructions.live ? (
        <>
          <p className="instructions">{instructions.live}</p>
          <p className="muted">
            {instructions.files.join(", ")}
            {instructions.updated_at && <> · él ezóta: {datetime(instructions.updated_at)}</>}
          </p>
        </>
      ) : (
        <Empty>
          Nincs külön utasítás. Ha a mappába tesz egy dokumentumot, amelynek a nevében szerepel az
          „utasítás” szó, annak a tartalmát a chat minden válasznál követi.
        </Empty>
      )}
    </Card>
  );
}
